import { Field } from '@sgdocs/client';
import React, { useState } from 'react';
import { QueryClientProvider } from 'react-query';
import { v4 as uuidv4 } from 'uuid';
import { queryClient } from '../common/hooks/queryClient';
import { useQueryGetSearchableFields } from '../common/hooks/useQueryGetSearchableFields';
import { EWidgetsSgmDocs } from '../common/widget/enum';
import { ISortingParameter, SGMDocsDocumentSortingParameter } from './components/SGMDocsDocumentSortingParameter';

const defaultLabels: ILabels = {
  sortBy: 'Sort by:',
  parameter: 'parameter',
  parameters: 'parameters',
  none: 'None',
  firstParameter: 'First parameter',
  secondParameter: 'Second parameter',
  thirdParameter: 'Third parameter',
  searchAndSelect: 'Search & select...',
  apply: 'Apply',
  ascending: 'Ascending',
  descending: 'Descending',
  reset: 'Reset',
};

export interface ILabels {
  sortBy: string;
  parameter: string;
  parameters: string;
  none: string;
  firstParameter: string;
  secondParameter: string;
  thirdParameter: string;
  searchAndSelect: string;
  apply: string;
  ascending: string;
  descending: string;
  reset: string;
}

export interface IDocumentSortingComponentProps {
  modelId: string | null;
  workspaceId: string | null;
  onSortingChanged: (sortQuery: string) => void;
  defaultSortQuery?: string;
  modelJsonSchema?: any;
  token?: string | null;
  sgmDocsBaseUrl: string;
  labels?: ILabels;
}

const DocumentSortingComponent: React.FC<IDocumentSortingComponentProps> = ({
  workspaceId,
  modelId,
  onSortingChanged,
  defaultSortQuery,
  modelJsonSchema,
  token = null,
  sgmDocsBaseUrl,
  labels = defaultLabels,
}: IDocumentSortingComponentProps): JSX.Element => {
  const [toggleMenu, setToggleMenu] = useState<boolean>(false);
  const [sortingParameters, setSortingParameters] = useState<Map<number, ISortingParameter>>(
    buildSortingParameters(defaultSortQuery)
  );
  const {
    data: searchableFields,
    isLoading,
    isError,
  } = useQueryGetSearchableFields(
    { workspaceIds: workspaceId ? [workspaceId] : undefined, modelIds: modelId ? [modelId] : undefined, size: '200' },
    { token, sgmDocsBaseUrl, component: EWidgetsSgmDocs.DOCUMENT_SORTING }
  );

  if (isLoading) {
    return (
      <div className="btn-group" style={{ maxHeight: '40px' }}>
        <button
          id={'sgmDocsDocumentSortingLoading'}
          className="btn btn-lg btn-default dropdown-toggle"
          type="button"
          onClick={() => null}
        >
          {labels.sortBy} <span className="text-info ms-1 me-3">{labels.none}</span>
        </button>
      </div>
    );
  }

  if (!isError && searchableFields) {
    if (workspaceId || (!workspaceId && !modelId)) {
      // If on workspace level or no workspace/model id supplied, don't allow sorting on metadata
      searchableFields.fields = searchableFields.fields.filter((f) => f && f.name && !f.name.includes('metadata.'));
    }

    // Filter out technical & GDPR limited fields
    searchableFields.fields = searchableFields.fields.filter(
      (f) =>
        f &&
        f.name &&
        f.name.slice(-2) !== 'Id' &&
        !f.name.includes('documentVersionIdentifier.') &&
        !f.name.includes('applicationCreator') &&
        !f.name.includes('creator') &&
        f.name !== 'metadata'
    );

    // Order searchable fields by object (1. metadata fields 2. document fields 3. content fields)
    searchableFields.fields.sort((a, b) => {
      if (a.name.includes('metadata') || b.name.includes('metadata')) {
        // @ts-ignore
        return !a.name.includes('metadata') - !b.name.includes('metadata');
      }
      // @ts-ignore
      return a.name.includes('.') - b.name.includes('.');
    });

    // Put confidentiality level after destroy date
    const confidentialityLevelField = searchableFields.fields.filter((f) => f.name === 'confidentialityLevel')
      ? searchableFields.fields.filter((f) => f.name === 'confidentialityLevel')[0]
      : null;
    const destroyDateField = searchableFields.fields.filter((f) => f.name === 'destroyDate')
      ? searchableFields.fields.filter((f) => f.name === 'destroyDate')[0]
      : null;
    if (confidentialityLevelField && destroyDateField) {
      searchableFields.fields.splice(searchableFields.fields.indexOf(confidentialityLevelField), 1);
      searchableFields.fields.splice(
        searchableFields.fields.indexOf(destroyDateField) + 1,
        0,
        confidentialityLevelField
      );
    }
  }

  const onSortingParameterChanged = (sortingParameter: ISortingParameter): void => {
    const newSortingParameters = new Map(sortingParameters);
    const sortingParameterToUpdate = newSortingParameters.get(sortingParameter.id);

    if (sortingParameterToUpdate) {
      // If already exists, update it
      sortingParameterToUpdate.fieldName = sortingParameter.fieldName;
      sortingParameterToUpdate.ascending = sortingParameter.ascending;
    } else {
      // Else add it
      newSortingParameters.set(sortingParameter.id, sortingParameter);
    }

    setSortingParameters(newSortingParameters);
  };

  const buildSortQuery = (): void => {
    const sortingParametersAsc = Array.from(sortingParameters.values()).sort((a, b) => a.id - b.id); // Order by id asc

    let sortQuery = '';
    for (const sortingParameter of sortingParametersAsc) {
      if (sortingParameter && sortingParameter.fieldName) {
        if (sortQuery) {
          sortQuery += ',';
        }
        sortQuery += sortingParameter.ascending ? sortingParameter.fieldName : '-'.concat(sortingParameter.fieldName);
      }
    }

    onSortingChanged(sortQuery);
  };

  function buildSortingParameters(defaultValue?: string): Map<number, ISortingParameter> {
    const defaultSortingParameters = new Map();

    if (defaultValue) {
      if (defaultValue.includes(',')) {
        let id = 1;
        for (const sq of defaultValue.split(',')) {
          const descending = sq.indexOf('-') === 0;
          defaultSortingParameters.set(id, {
            id: id,
            fieldName: descending ? sq.substring(1) : sq,
            ascending: !descending,
          });
          id++;
        }
      } else {
        const descending = defaultValue.indexOf('-') === 0;
        defaultSortingParameters.set(1, {
          id: 1,
          fieldName: descending ? defaultValue.substring(1) : defaultValue,
          ascending: !descending,
        });
      }
    }

    return defaultSortingParameters;
  }

  /**
   * Filter already selected fields for specific sorting parameter
   * @param sortingParameterId
   * @param fields
   */
  function filterSearchableFields(sortingParameterId: number, fields: Field[] | undefined): Field[] {
    if (fields) {
      let newFields: Field[] = [...fields];
      for (const sp of sortingParameters.values()) {
        if (sp && sp.fieldName !== '' && sp.id !== sortingParameterId) {
          newFields = newFields.filter((f) => f && f.name !== sp.fieldName);
        }
      }
      const seen = new Set<string>();
      return newFields.filter((sf) => {
        if (!sf.name || seen.has(sf.name)) return false;
        seen.add(sf.name);
        return true;
      });
    }
    return [];
  }

  function reset(): void {
    setSortingParameters(
      new Map()
        .set(1, { id: 1, fieldName: '', ascending: true })
        .set(2, { id: 2, fieldName: '', ascending: true })
        .set(3, { id: 3, fieldName: '', ascending: true })
    );
  }

  let nbOfFilledSortingParameters = 0;
  for (const sp of sortingParameters.values()) {
    if (sp && sp.fieldName) {
      nbOfFilledSortingParameters++;
    }
  }

  return (
    <div className="btn-group" style={{ maxHeight: '40px' }}>
      <button
        id={'sgmDocsDocumentSorting'}
        className="btn btn-lg btn-default dropdown-toggle"
        type="button"
        onClick={(): void => setToggleMenu(!toggleMenu)}
      >
        {labels.sortBy}{' '}
        <span className="text-info ms-1 me-3">
          {nbOfFilledSortingParameters > 0
            ? nbOfFilledSortingParameters > 1
              ? nbOfFilledSortingParameters + ` ${labels.parameters}`
              : nbOfFilledSortingParameters + ` ${labels.parameter}`
            : labels.none}
        </span>
      </button>
      <div className={'dropdown-menu dropdown-menu-right p-3 ' + (toggleMenu ? 'show' : '')} style={{ width: '400px' }}>
        <SGMDocsDocumentSortingParameter
          key={'sgmDocsDocumentSortingParameter-' + 1}
          id={1}
          label={labels.firstParameter}
          searchableFields={filterSearchableFields(1, searchableFields?.fields)}
          onChanged={onSortingParameterChanged}
          selected={sortingParameters.get(1)}
          modelJsonSchema={modelJsonSchema}
          labels={labels}
        />
        <SGMDocsDocumentSortingParameter
          key={'sgmDocsDocumentSortingParameter-' + 2}
          id={2}
          label={labels.secondParameter}
          searchableFields={filterSearchableFields(2, searchableFields?.fields)}
          onChanged={onSortingParameterChanged}
          selected={sortingParameters.get(2)}
          modelJsonSchema={modelJsonSchema}
          labels={labels}
        />
        <SGMDocsDocumentSortingParameter
          key={'sgmDocsDocumentSortingParameter-' + 3}
          id={3}
          label={labels.thirdParameter}
          searchableFields={filterSearchableFields(3, searchableFields?.fields)}
          onChanged={onSortingParameterChanged}
          selected={sortingParameters.get(3)}
          modelJsonSchema={modelJsonSchema}
          labels={labels}
        />

        <div className={'d-flex justify-content-end mt-4'}>
          <button className="btn btn-lg btn-outline-secondary me-2" type="button" onClick={reset}>
            {labels.reset}
          </button>
          <button
            className="btn btn-lg btn-primary"
            type="button"
            onClick={(): void => {
              buildSortQuery();
              setToggleMenu(false);
            }}
          >
            {labels.apply}
          </button>
        </div>
      </div>
    </div>
  );
};

export const SGMDocsDocumentSorting: React.FC<IDocumentSortingComponentProps> = ({
  workspaceId,
  modelId,
  onSortingChanged,
  defaultSortQuery,
  modelJsonSchema,
  token,
  sgmDocsBaseUrl,
  labels,
}: IDocumentSortingComponentProps) => {
  return (
    <QueryClientProvider client={queryClient}>
      <DocumentSortingComponent
        key={uuidv4()}
        workspaceId={workspaceId}
        modelId={modelId}
        onSortingChanged={onSortingChanged}
        defaultSortQuery={defaultSortQuery}
        modelJsonSchema={modelJsonSchema}
        token={token}
        sgmDocsBaseUrl={sgmDocsBaseUrl}
        labels={labels}
      />
    </QueryClientProvider>
  );
};
